import { Component, inject, input, InputSignal, ViewChild } from '@angular/core';
import { MatMenu, MatMenuItem } from '@angular/material/menu';

import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { SprayTypes } from 'src/app/shared/models';

import { DownyMildewSignalStore } from '../../signal-store/downy-mildew-signal-store';
import { createDownyMildewTelemetryProperties, DownyMildewAIIdentifiers } from '../constants';
import { MatDivider } from '@angular/material/divider';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';
import { AppState } from '@app/shared/models';

@Component({
  selector: 'app-downy-mildew-action-menu',
  template: `
    <mat-menu #actionMenu="matMenu">
      <button mat-menu-item (click)="onSprayClicked()">
        {{ 'downyMildew.actionMenu.spray' | translate }}
      </button>
      <button mat-menu-item (click)="onIrrigateClicked()">
        {{ 'downyMildew.actionMenu.irrigation' | translate }}
      </button>
      <mat-divider />
      <button mat-menu-item (click)="onRegisterGrowthStageClicked()">
        {{ 'downyMildew.actionMenu.registerGrowthStage' | translate }}
      </button>
      <button mat-menu-item (click)="onRegisterObservationClicked()">
        {{ 'downyMildew.actionMenu.registerObservation' | translate }}
      </button>
    </mat-menu>
  `,
  imports: [MatMenu, MatMenuItem, MatDivider, TranslatePipe],
})
export class DownyMildewActionMenuComponent {
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly downyMildewStore = inject(DownyMildewSignalStore);
  private readonly locale = inject(LocaleService);

  @ViewChild('actionMenu', { static: true }) public menu!: MatMenu;

  protected sprayTypes: typeof SprayTypes = SprayTypes;

  readonly advices: InputSignal<CropFieldAdvice[]> = input.required<CropFieldAdvice[]>();
  readonly actionDate: InputSignal<Date | undefined> = input<Date | undefined>();
  readonly context = input<'overview' | 'insights' | undefined>(undefined);

  protected onSprayClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('spray', DownyMildewAIIdentifiers.selectPreventive);
    this.downyMildewStore.openSpraysDialog(
      SprayTypes.PreventiveSpray,
      this.advices(),
      this.actionDate(),
    );
  }

  protected onIrrigateClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('irrigation', DownyMildewAIIdentifiers.selectIrrigation);
    this.downyMildewStore.openBlightIrrigation(this.advices(), this.actionDate());
  }

  protected onRegisterGrowthStageClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('registerGrowthStage', DownyMildewAIIdentifiers.selectGrowthStage);
    this.downyMildewStore.openGrowthStageDialog(this.advices(), this.actionDate());
  }

  protected onRegisterObservationClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('registerObservation', DownyMildewAIIdentifiers.selectObservation);
    this.downyMildewStore.openObservationDialog(this.advices(), this.actionDate());
  }

  private trackActionMenuItemSelected(actionType: string, legacyIdentifier: string): void {
    const context = this.context();
    if (!context) {
      this.ai.trackEvent(legacyIdentifier, {});
      return;
    }

    const eventIdentifier =
      context === 'insights'
        ? DownyMildewAIIdentifiers.insightsActionMenuItemSelected
        : DownyMildewAIIdentifiers.overviewActionMenuItemSelected;

    this.ai.trackEvent(
      eventIdentifier,
      createDownyMildewTelemetryProperties('actionMenuItemSelected', {
        actionType,
      }),
    );
  }

  private notifyMutationUnavailable(): void {
    this.appStore.dispatch(
      NotificationActions.infoSnackbar({
        message: this.locale.translate('UI.CropYearUnavailableMessage'),
      }),
    );
  }
}
